// src/components/SurveyForm/Questions/RadioQuestion/RadioQuestion.jsx

import React, { Fragment } from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

/**
 * Componente para preguntas de opción única (radio buttons)
 * Soporta opción "Otro" con campo de texto
 */
const RadioQuestion = ({
  questionName,
  description,
  options = [],
  currentValue = '',
  otherValue = '',
  questionId,
  typeQuestion,
  index,
  conditional = false,
  hasError = false,
  submitAttempted = false,
  primaryColor = '#1976d2',
  questionRef,
  selectedRadioOptions = {},
  onValueChange,
  onOtherInputChange,
  hasOwnBold,
}) => {
  /**
   * Verifica si la opción "Otro" está seleccionada
   */
  const isOtherSelected = () => {
    const selectedOptionName = selectedRadioOptions[questionId];
    const selectedOption = options.find(
      (opt) => opt.optionName === selectedOptionName
    );
    return selectedOption?.isOther === true;
  };

  /**
   * Maneja el cambio de selección
   */
  const handleChange = (event) => {
    const selectedValue = event.target.value;
    const selectedOption = options.find(
      (opt) => opt.optionName === selectedValue
    );

    onValueChange(selectedValue, selectedOption);
  };

  /**
   * Renderiza una opción individual
   */
  const renderOption = (option, optionIndex) => {
    const { numberOption, optionName } = option;

    return (
      <FormControlLabel
        ref={optionIndex === 0 ? questionRef : null}
        key={numberOption}
        value={optionName}
        control={
          <Radio
            sx={{
              color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
              '&.Mui-checked': {
                color: primaryColor,
              },
              padding: '0px 9px 9px 9px',
            }}
          />
        }
        label={<Box>{optionName}</Box>}
        style={{
          fontSize: '0.5em !important',
          width: '100%',
          alignItems: 'start',
        }}
      />
    );
  };

  return (
    <Fragment>
      {/* Título de la pregunta */}
      <FormLabel
        id={`${questionId}-${typeQuestion}`}
        style={{
          fontSize: '1.1em',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '0.8em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
        ref={questionRef}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {/* Descripción opcional */}
      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      {/* Grupo de radio buttons */}
      <RadioGroup
        name={`${questionId}-${typeQuestion}`}
        value={selectedRadioOptions[questionId] || currentValue}
        onChange={handleChange}
        row
        style={{
          margin: '1.2em 0',
          color: hasError ? 'red' : 'inherit',
        }}
      >
        {options.map((option, optionIndex) =>
          renderOption(option, optionIndex)
        )}
      </RadioGroup>

      {/* Campo de texto para "Otro" */}
      {isOtherSelected() && (
        <TextField
          fullWidth
          autoFocus
          required
          variant="outlined"
          placeholder="Especifica tu respuesta..."
          value={otherValue}
          onChange={onOtherInputChange}
          // Sin este aviso la pregunta queda en rojo y no se entiende que falta escribir
          error={hasError && (!otherValue || otherValue.trim() === '')}
          helperText={
            hasError && (!otherValue || otherValue.trim() === '')
              ? 'Escribe tu respuesta para poder continuar'
              : ''
          }
          sx={{
            marginTop: '1em',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
              },
            },
            '& label.Mui-focused': {
              color: primaryColor,
            },
          }}
        />
      )}

      <Divider variant="middle" sx={{ marginTop: '1em' }} />
    </Fragment>
  );
};

RadioQuestion.propTypes = {
  questionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      numberOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      optionName: PropTypes.string.isRequired,
      isOther: PropTypes.bool,
      childrenQuestionNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    })
  ).isRequired,
  currentValue: PropTypes.string,
  otherValue: PropTypes.string,
  questionId: PropTypes.number.isRequired,
  typeQuestion: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  conditional: PropTypes.bool,
  hasError: PropTypes.bool,
  submitAttempted: PropTypes.bool,
  primaryColor: PropTypes.string,
  questionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  selectedRadioOptions: PropTypes.object,
  onValueChange: PropTypes.func.isRequired,
  onOtherInputChange: PropTypes.func.isRequired,
};

export default RadioQuestion;
